import { useEffect, useState } from "react";
import { PageHeader } from "../components/PageHeader";
import { AuthRequired } from "../components/AuthRequired";
import {
  Banner,
  Card,
  Chip,
  Disclosure,
  Kv,
  KvList,
  SectionLabel,
  Stack,
} from "../ui/primitives";
import { IconShield } from "../ui/icons";
import {
  getOrCreateDeviceFingerprintHash,
  _readRawDeviceFingerprintForTesting,
} from "../auth/device-fingerprint";
import {
  getSessionMetadata,
  subscribeSessionMetadata,
  type SessionMetadata,
} from "../auth/session";

// Trusted devices.
//
// ─── What this page can and cannot show, and why ──────────────────────────
//
// The operator's brief asks for a card per device with Last seen, a
// Trusted/Untrusted chip, and TRUST / REVOKE / VIEW actions. None of that is
// buildable from a browser today, and shipping it would mean shipping either
// invented data or dead buttons.
//
// The device LIST lives behind `/v2/admin/owner/devices`, which the
// gao-id-worker gates with `authorizeAdminControl(SCOPE_ADMIN_CONTROL_READ)` —
// H-2 service-auth, i.e. an HMAC signature made with a shared secret that must
// never leave a trusted operator workstation. The route's own comment is
// explicit that a trusted-device-bound admin session is Impl-E work and is not
// wired. So: no list, no per-device last-seen, no trust state, and no trust or
// revoke mutation. That is a backend gap, and it is stated on the page rather
// than papered over.
//
// What the browser CAN see is exactly one device — this one — and it can see
// it honestly, because the fingerprint is computed here. So the page renders
// one real card instead of a fabricated fleet, and keeps the BLOCKED notice
// for everything else.

function useDeviceFingerprint(): { hash: string | null | undefined; present: boolean } {
  const [hash, setHash] = useState<string | null | undefined>(undefined);
  const [present, setPresent] = useState(false);
  useEffect(() => {
    // Presence is read BEFORE the hash call, because
    // `getOrCreateDeviceFingerprintHash` creates the row when it is missing —
    // so asking afterwards would always answer "yes" and the distinction
    // between a returning and a first-time browser would be lost.
    setPresent(_readRawDeviceFingerprintForTesting() !== null);
    let live = true;
    void getOrCreateDeviceFingerprintHash()
      .then((h) => live && setHash(h))
      .catch(() => live && setHash(null));
    return () => {
      live = false;
    };
  }, []);
  return { hash, present };
}

function ThisBrowserCard({ session }: { session: SessionMetadata | null }) {
  const { hash, present } = useDeviceFingerprint();

  const state =
    hash === undefined
      ? { label: "CHECKING", tone: "neutral" as const }
      : hash === null
        ? { label: "NO FINGERPRINT", tone: "warn" as const }
        : present
          ? { label: "FINGERPRINT PRESENT", tone: "ok" as const }
          : { label: "NEW FINGERPRINT", tone: "info" as const };

  return (
    <Card
      title="This browser"
      note="The only device this console can see from here"
      actions={
        <Chip tone={state.tone} solid>
          {state.label}
        </Chip>
      }
    >
      <KvList>
        <Kv k="Signed in as">
          {session ? (
            <code className="g-mono">{session.ownerSubject}</code>
          ) : (
            <span style={{ color: "var(--fg-dim)" }}>no active session</span>
          )}
        </Kv>
        <Kv k="Session started">
          {session ? (
            <code className="g-mono">{new Date(session.savedAt).toISOString()}</code>
          ) : (
            <span style={{ color: "var(--fg-dim)" }}>—</span>
          )}
        </Kv>
        <Kv k="Fingerprint">
          {hash === undefined ? (
            <span style={{ color: "var(--fg-dim)" }}>computing…</span>
          ) : hash === null ? (
            <span style={{ color: "var(--warn)" }}>
              unavailable in this browser — sign-in will not be device-bound
            </span>
          ) : (
            <code className="g-mono" title={hash}>
              {hash.slice(0, 16)}…{hash.slice(-8)}
            </code>
          )}
        </Kv>
      </KvList>

      <Disclosure summary="Technical details">
        <div style={{ paddingTop: 10 }}>
          <KvList dense>
            <Kv k="Full hash">
              {hash ? (
                <code className="g-mono" style={{ wordBreak: "break-all" }}>
                  {hash}
                </code>
              ) : (
                "—"
              )}
            </Kv>
            <Kv k="Derivation">SHA-256 of a 32-byte random value held by this browser</Kv>
            <Kv k="Storage">
              <code>localStorage</code> — survives a browser restart, unlike the session
            </Kv>
            <Kv k="Sent as">
              <code>deviceFingerprintHash</code> on the owner-auth verify call
            </Kv>
          </KvList>
          <p style={{ fontSize: 12.5, color: "var(--fg-dim)", margin: "12px 0 0" }}>
            Clearing this browser's site data resets the fingerprint. The next sign-in
            generates a new one, which the operator must re-approve out of band — this console
            cannot approve it.
          </p>
        </div>
      </Disclosure>
    </Card>
  );
}

export function Devices() {
  const [session, setSession] = useState<SessionMetadata | null>(getSessionMetadata());
  useEffect(() => subscribeSessionMetadata(setSession), []);

  return (
    <div>
      <PageHeader
        title="Devices"
        subtitle="Devices trusted to hold an owner session."
        meta={
          <Chip tone="warn" solid>
            FLEET LIST UNAVAILABLE
          </Chip>
        }
      />
      <AuthRequired>
        <Stack gap={18}>
          <ThisBrowserCard session={session} />

          <Stack gap={10}>
            <SectionLabel>Other devices</SectionLabel>
            {/* Not a BlockedScreen full of protocol names any more, but the
                same fact, and it stays on the default surface rather than
                inside a disclosure: an operator must not conclude from a page
                titled "Devices" showing one card that they have one device. */}
            <Banner tone="warn" title="This console cannot list your other devices">
              The fleet list is served by an operator-only route that requires a shared secret,
              so a browser may not call it. Listing, trusting and revoking devices are done from
              the operator workstation until a session-authenticated route exists.
            </Banner>
            <Disclosure summary="Why, and what would unblock it">
              <div style={{ paddingTop: 10, fontSize: 13, color: "var(--fg-muted)" }}>
                <KvList dense>
                  <Kv k="Route">
                    <code>/v2/admin/owner/devices</code>
                  </Kv>
                  <Kv k="Gate">H-2 service-auth (HMAC)</Kv>
                  <Kv k="Why blocked">
                    The HMAC secret must never leave a trusted operator workstation, so the
                    browser cannot hold one.
                  </Kv>
                  <Kv k="Unblocked by">
                    A session-auth BFF proxy at <code>/v2/admin/bff/devices</code> that re-signs
                    server-side. It must go through the Impl-E{" "}
                    <code>enforceAdminControlAccess</code> gate, never the raw HMAC verifier.
                  </Kv>
                </KvList>
              </div>
            </Disclosure>
          </Stack>

          <Banner tone="info">
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
              <IconShield size={14} />
              Device binding is enforced by the backend at sign-in, not by this screen.
            </span>
          </Banner>
        </Stack>
      </AuthRequired>
    </div>
  );
}
